// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MatSlideToggleChange, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertyTagNfcsService } from 'src/app/modules/properties/propertyTagNfcs.service';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */

@Component({
  selector: "app-property-tag-nfc-edit-dialog",
  templateUrl: "./property-tag-nfc-edit-dialog.component.html",
  styleUrls: ["./property-tag-nfc-edit-dialog.component.scss"],
})
export class PropertyTagNfcEditDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  enableEdit = false;

  // BEGIN - Edit phonebook contact form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {
    formState: {
      disabled: true
    }
  };
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit phonebook contact form

  loggedUserMaster = false;
  pageTitle = '';

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyTagNfcEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private propertyTagNfcsService: PropertyTagNfcsService,
    public confirmDialog: MatDialog
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    const currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    if (
      currentRole.role.type === "master" ||
      currentRole.role.type === "collaborator"
    ) {
      this.loggedUserMaster = true;
      this.pageTitle = this.translateService.instant("propertyTagNfc.dialog.view.title");
    } else {
      this.pageTitle = this.translateService.instant("propertyTagNfc.dialog.edit.title");
    }

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init add form
    if (this.loggedUserMaster) {
      this.initMasterForm();
    } else {
      this.initForm();
    }

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    if (this.data) {
      let createdBy = '';
      if (
        this.data.createdBy && 
        this.data.createdBy.firstName
      ) {
        createdBy = `${this.data.createdBy.firstName} ${this.data.createdBy.lastName}`
      }
      let updatedBy = '';
      if (
        this.data.updatedBy && 
        this.data.updatedBy.firstName
      ) {
        updatedBy = `${this.data.updatedBy.firstName} ${this.data.updatedBy.lastName}`
      }

      if (this.enableEdit) {
        this.options.formState = {
          disabled: false
        };
      }

      this.model["files"] = this.data.images;

      this.fields.push({
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "file-uploader-structure",
            key: "files",
            defaultValue: this.data.images,
            templateOptions: {
              required: false,
              allowedFilesNumber: 3,
              allowedFilesExtensions: 'jpg,jpeg,png,gif',
              customLabel: this.translateService.instant(
                "propertyTagNfc.fields.images"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "uid",
            defaultValue: this.data.uid,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.uid"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.uid"
              ),
              required: true,
              minLength: 0,
              maxLength: 1000,
              rows: 4,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "name",
            defaultValue: this.data.name,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.name"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.name"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "select",
            key: "shortDescription",
            defaultValue: this.data.shortDescription,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.shortDescription"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.shortDescription"
              ),
              required: false,
              options: [
                {
                  value: "externalPushButtonPanel",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.externalPushButtonPanel"),
                },
                {
                  value: "understairsPushButtonPanel",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.understairsPushButtonPanel"),
                },
                {
                  value: "dedicatedLocalAccessDoor",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.dedicatedLocalAccessDoor"),
                },
                {
                  value: "driveway",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.driveway"),
                },
                {
                  value: "other",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.other"),
                },
              ],
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            defaultValue: this.data.description,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.description"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.description"
              ),
              required: false,
              minLength: 0,
              maxLength: 1000,
              rows: 4
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "latitude",
            defaultValue: this.data.latitude,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.latitude"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.latitude"
              ),
              required: false,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "longitude",
            defaultValue: this.data.longitude,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.longitude"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.longitude"
              ),
              required: false,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "datepicker",
            key: "installationDate",
            defaultValue: this.data.installationDate,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.installationDate"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.installationDate"
              ),
              required: true,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "createdBy",
            defaultValue: createdBy,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.createdBy"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.createdBy"
              ),
              required: false,
              disabled: true
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "datepicker",
            key: "createdAt",
            defaultValue: this.data.createdAt,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.createdAt"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.createdAt"
              ),
              required: false,
              disabled: true
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "updatedBy",
            defaultValue: updatedBy,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.updatedBy"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.updatedBy"
              ),
              required: false,
              disabled: true
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "datepicker",
            key: "updatedAt",
            defaultValue: this.data.updatedAt,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.updatedAt"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.updatedAt"
              ),
              required: false,
              disabled: true
            },
            wrappers: ['form-field-horizontal']
          },
        ],
      });
    }
  }

  /**
   * Init master form
   *
   * @since 1.0.0
   */
  initMasterForm(): void {
    if (this.data) {
      const apiHost = this.configService.apiHost;
      let imagesContainer = '';
      if (this.data.images && Array.isArray(this.data.images)) {
        for (const image of this.data.images) {
          const path = image.path;
          // if (path.startsWith("public")) {
          if (path.substring(0,apiHost.length) !== apiHost) {
            const newPath = `${apiHost}\\${path}`;
            image.path = newPath;
          }
          imagesContainer = `${imagesContainer}<img src="${image.path}" alt="${image.originalName}">`
        }
      }
      this.fields.push({
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 boxForm",
            template:
              `<div class="img-preview-container">
              ${imagesContainer}
              </div><br />`,
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "name",
            defaultValue: this.data.name,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.name"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.name"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "select",
            key: "shortDescription",
            defaultValue: this.data.shortDescription,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.shortDescription"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.shortDescription"
              ),
              required: false,
              options: [
                {
                  value: "externalPushButtonPanel",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.externalPushButtonPanel"),
                },
                {
                  value: "understairsPushButtonPanel",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.understairsPushButtonPanel"),
                },
                {
                  value: "dedicatedLocalAccessDoor",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.dedicatedLocalAccessDoor"),
                },
                {
                  value: "driveway",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.driveway"),
                },
                {
                  value: "other",
                  label: this.translateService.instant("propertyTagNfc.shortDescription.other"),
                },
              ],
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            defaultValue: this.data.description,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.description"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.description"
              ),
              required: false,
              minLength: 0,
              maxLength: 1000,
              rows: 4
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "datepicker",
            key: "installationDate",
            defaultValue: this.data.installationDate,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.installationDate"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.installationDate"
              ),
              required: false,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "latitude",
            defaultValue: this.data.latitude,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.latitude"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.latitude"
              ),
              required: false,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
          {
            className: "col-12",
            type: "input",
            key: "longitude",
            defaultValue: this.data.longitude,
            templateOptions: {
              label: this.translateService.instant("propertyTagNfc.fields.longitude"),
              placeholder: this.translateService.instant(
                "propertyTagNfc.fields.longitude"
              ),
              required: false,
              disabled: true,
            },
            wrappers: ['form-field-horizontal']
          },
        ],
      });
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
   onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }
    if (model && model.files && Array.isArray(model.files)) {
      for (const image of model.files) {
        image.path = image.path.replace(`${this.configService.apiHost}`, "");
        image.path = image.path.replace(/^[\\]*uploads/,"uploads");
      }
    }
    const sendData = {
      images: model.files,
      uid: model.uid,
      name: model.name,
      shortDescription: model.shortDescription,
      description: model.description,
      latitude: model.latitude,
      longitude: model.longitude,
      installationDate: model.installationDate,
      createdBy: model.createdBy,
      updatedBy: model.updatedBy,
      createdAt: model.createdAt,
      updatedAt: model.updatedAt      
    };

    this.subscriptions.push(
      this.propertyTagNfcsService.updateFields(
        this.data._id,
        sendData,
      )
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose(res);
              return;
            }
            this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
   onClose(data?: any): void {
    this.dialogRef.close(data);
  }
  
    /**
     * Toggle activation in all list elements
     *
     * @since 1.0.0
     */
    onToggleSelected(element: MatSlideToggleChange): void {
      if (!element.checked) {
        // Prevent invalid form submit
        if (this.form.invalid) {
          Object.keys(this.form.controls).forEach((key) => {
            this.form.controls[key].markAsTouched();
          });
          this.toastrService.error(
            this.translateService.instant("common.errors.form_is_invalid")
          );
          element.source.toggle();
          return;
        }
      }
      this.enableEdit = element.checked;
      if (this.enableEdit) {
        this.options.formState = {
          disabled: false
        };
      } else {
        this.options.formState = {
          disabled: true
        };
      }
    }
}
